import { FC, useMemo, useRef, useState } from "react"
import clsx from "clsx"

import { useProps } from "contexts/PropsContext"

import { useClickOutside } from "hooks/useClickOutside"

export type SearchableDropdownOption = { value: string; label: string; extra?: string }

export interface SearchableDropdownProps {
  mode: "preloaded" | "search"
  options: SearchableDropdownOption[]
  selected: string[]
  selectedOptions?: SearchableDropdownOption[]
  onSelect: (ids: string[]) => void
  loading: boolean
  searchValue: string
  onSearchChange: (q: string) => void
  placeholder?: string
  emptyLabel?: string
  noResultsLabel?: string
  minSearchChars?: number
  single?: boolean // One value at most
}

export const SearchableDropdown: FC<SearchableDropdownProps> = ({
  mode,
  options,
  selected,
  selectedOptions = [],
  onSelect,
  loading,
  searchValue,
  onSearchChange,
  placeholder = "Search...",
  emptyLabel = "No items loaded",
  noResultsLabel = "No results found",
  minSearchChars = 2,
  single = false
}) => {
  const { darkMode } = useProps()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  useClickOutside(rootRef, () => setOpen(false), open)

  // For preloaded mode, filter locally; for search mode, merge results + selected
  const displayOptions = useMemo(() => {
    if (mode === "search") {
      const resultIds = new Set(options.map((o) => o.value))
      const missingSelected = selectedOptions.filter((s) => !resultIds.has(s.value))
      return [...missingSelected, ...options]
    }
    // preloaded: local filter
    const q = searchValue.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q) || o.extra?.toLowerCase().includes(q)
    )
  }, [mode, options, selectedOptions, searchValue])

  const selectedLabels = useMemo(() => {
    if (!selected.length) return "None selected"
    const allOpts = [...options, ...selectedOptions]
    const seen = new Set<string>()
    const unique = allOpts.filter((o) => {
      if (seen.has(o.value)) return false
      seen.add(o.value)
      return true
    })
    const matched = unique.filter((o) => selected.includes(o.value))
    if (matched.length === 0) return `${selected.length} selected`
    if (matched.length <= 3) return matched.map((m) => m.label).join(", ")
    return `${matched.length} selected`
  }, [selected, options, selectedOptions])

  const toggleOne = (id: string) => {
    if (single) {
      onSelect([id])
      onSearchChange("")
      setOpen(false)
      return
    }
    if (selected.includes(id)) onSelect(selected.filter((x) => x !== id))
    else onSelect([...selected, id])
  }

  const clear = () => onSelect([])

  const showPrompt = mode === "search" && searchValue.trim().length < minSearchChars && options.length === 0

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        className={clsx(
          "w-full flex items-center justify-between px-4 py-2 border rounded-lg text-left cursor-pointer",
          "border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]",
          "dark:bg-[rgba(0,0,0,0.2)] dark:text-white",
          "focus:border-[rgba(139,92,246,0.5)] focus:outline-none transition-all"
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="truncate pr-6 text-sm">{selectedLabels}</span>
        <span className="opacity-70 text-xs">▾</span>
      </button>

      {open && (
        <div
          className={clsx(
            "absolute left-0 top-[calc(100%+4px)] w-full z-50 rounded-xl border shadow-lg",
            darkMode ? "bg-[#0C0C1A] border-[rgba(255,255,255,0.1)]" : "bg-white border-gray-200"
          )}
        >
          {/* Header — multi-select only; see the `single` prop. */}
          {!single && (
            <div className="p-3 border-b border-[rgba(255,255,255,0.06)] flex items-center justify-between gap-2">
              <span className="text-sm opacity-80">{selected.length} selected</span>
              <button type="button" className="text-xs underline text-red-400 hover:text-red-300" onClick={clear}>
                Clear
              </button>
            </div>
          )}

          {/* Search */}
          <div className="p-2 border-b border-[rgba(255,255,255,0.06)]">
            <input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={placeholder}
              autoFocus
              className={clsx(
                "w-full px-3 py-1.5 text-sm border rounded-lg",
                "border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]",
                "dark:bg-[rgba(0,0,0,0.3)] dark:text-white focus:outline-none"
              )}
            />
          </div>

          {/* Options list */}
          <div className="max-h-64 overflow-auto p-1">
            {loading ? (
              <div className="p-3 text-sm opacity-70 text-center">Loading...</div>
            ) : showPrompt ? (
              <div className="p-3 text-sm opacity-70 text-center">Type at least {minSearchChars} characters to search...</div>
            ) : displayOptions.length === 0 ? (
              <div className="p-3 text-sm opacity-70 text-center">{options.length === 0 ? emptyLabel : noResultsLabel}</div>
            ) : (
              displayOptions.map((opt) => {
                const checked = selected.includes(opt.value)
                return (
                  <label
                    key={opt.value}
                    className={clsx(
                      "flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer",
                      darkMode ? "hover:bg-[rgba(255,255,255,0.06)]" : "hover:bg-gray-100",
                      checked && (darkMode ? "bg-[rgba(139,92,246,0.15)]" : "bg-purple-50")
                    )}
                  >
                    <input
                      type={single ? "radio" : "checkbox"}
                      checked={checked}
                      onChange={() => toggleOne(opt.value)}
                      className={clsx("w-4 h-4", !single && "rounded")}
                    />
                    <span className="text-sm flex-1">{opt.label}</span>
                    {opt.extra && <span className="text-xs opacity-50">{opt.extra}</span>}
                  </label>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
